'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { PrimaryCTA } from 'components/shared/CTAComponent';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import {
  HERO_ROUTES,
  cleanHeroReturnTo,
  isHeroesPath,
  type HeroCategory,
  type HeroVerifyFailure,
} from 'lib/commerce/heroes/heroVerification';
import { openGovxVerify } from '../govxConfig';
import { IdmeVerifyButton } from '../IdmeVerifyButton';
import styles from './index.module.css';

/** Only GovX returns to the site with a failure; ID.me's Discountify app owns its errors. */
export type HeroVerifyError = {
  category: HeroCategory;
  provider: 'govx';
  reason: HeroVerifyFailure;
};

type Step =
  | { kind: 'route' }
  | ({ kind: 'error' } & HeroVerifyError)
  | { kind: 'redirecting' };

export type HeroVerifyModalProps = {
  /** Entry page: success lands on its buy page, failure reopens the modal here. */
  returnTo: string;
  initialError?: HeroVerifyError;
};

/** GovX's return step: the failure, with a retry and the ID.me alternative. */
export function HeroVerifyModal({
  returnTo: rawReturnTo,
  initialError,
}: HeroVerifyModalProps) {
  const t = useTranslations('common');
  const { region } = useRegionLocale();
  const returnTo = cleanHeroReturnTo(rawReturnTo);
  const startedOnHeroes = isHeroesPath(returnTo);
  const surface = startedOnHeroes ? 'heroes' : 'buy';

  const [step, setStep] = useState<Step>(() =>
    initialError ? { kind: 'error', ...initialError } : { kind: 'route' },
  );

  const renderIdmeButton = (category: HeroCategory) => (
    <IdmeVerifyButton
      category={category}
      surface={surface}
      className={styles.idmeButton}
    />
  );

  const startGovx = () => {
    setStep({ kind: 'redirecting' });
    openGovxVerify(surface, region, returnTo);
  };

  const renderRoute = () => (
    <>
      <h2 className={styles.title}>{t('heroVerify.providerStep.title')}</h2>
      <div className={styles.options}>
        <button type="button" className={styles.option} onClick={startGovx}>
          <span className={styles.optionTitle}>
            {t('heroVerify.providers.govx.title')}
          </span>
          <span className={styles.optionDescription}>
            {t('heroVerify.providers.govx.description')}
          </span>
        </button>
      </div>
      {renderIdmeButton('veterans')}
      <p className={styles.disclaimer}>{t('heroVerify.disclaimer')}</p>
    </>
  );

  const renderError = ({ category, reason }: HeroVerifyError) => (
    <>
      <h2 className={styles.title}>{t('heroVerify.error.title')}</h2>
      <p className={styles.body} role="alert">
        {t(`heroVerify.error.${reason === 'denied' ? 'error' : reason}`)}
      </p>
      <div className={styles.ctas}>
        <PrimaryCTA color="black" onClick={startGovx}>
          {t('heroVerify.error.retry')}
        </PrimaryCTA>
      </div>
      {HERO_ROUTES[category].providers.includes('idme') ? (
        <>
          <p className={styles.body}>{t('heroVerify.error.orIdme')}</p>
          {renderIdmeButton(category)}
        </>
      ) : null}
    </>
  );

  return (
    <div className={styles.root} data-testid="hero-verify-modal">
      {step.kind === 'error' ? (
        <button
          type="button"
          className={styles.back}
          onClick={() => setStep({ kind: 'route' })}
        >
          {t('heroVerify.back')}
        </button>
      ) : null}
      {step.kind === 'route' && renderRoute()}
      {step.kind === 'error' && renderError(step)}
      {step.kind === 'redirecting' && (
        <p className={styles.body} aria-live="polite">
          {t('heroVerify.redirecting')}
        </p>
      )}
    </div>
  );
}
